import {useEffect, useRef} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {persistQueryClient} from '@tanstack/react-query-persist-client';
import {createFileRoute, redirect, useLocation, useNavigate} from '@tanstack/react-router';
import {Toaster} from 'sonner';

import {CACHE_BUSTER} from '@/lib/cache-buster';
import {getToken} from '@/lib/auth-token';
import {createIDBPersister} from '@/lib/offline/persist';
import {canAccessRoute, homePathFor} from '@/lib/permissions';
import {useAuthStore} from '@/store/auth';
import {useAuth} from '@/hooks/useAuth';
import {AuthenticatedLayout} from '@/layouts/AuthenticatedLayout';

const PERSIST_MAX_AGE = 24 * 60 * 60 * 1000;

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: ({location}) => {
    const {user, isAuthenticated, homePage, clearUser} = useAuthStore.getState();
    if (isAuthenticated && user) {
      // Hydrated state — enforce route-level access. Catches initial loads
      // and any navigation where the layout's beforeLoad fires.
      if (!canAccessRoute(location.pathname, user)) {
        const target = homePathFor(user, homePage);
        // If the redirect target is itself inaccessible (or is the page we
        // just denied), redirecting would loop. Force logout and bounce to
        // /login — it indicates the user's account has no accessible route.
        if (target !== location.pathname && canAccessRoute(target, user)) {
          throw redirect({to: target});
        }
        clearUser();
        throw redirect({to: '/login'});
      }
      return;
    }
    // Optimistic: a token in localStorage means we're probably authenticated
    // but Zustand hasn't been hydrated yet (e.g. on a hard page refresh).
    // useAuth will validate via whoami; if the token is invalid, the 401
    // interceptor in client.ts redirects to /login?session_expired=true.
    // The runtime check in AuthenticatedGuard handles permission enforcement
    // after hydration.
    if (getToken()) return;
    throw redirect({to: '/login'});
  },
  component: AuthenticatedGuard,
});

function AuthenticatedGuard() {
  const {isAuthenticated, isLoading, user} = useAuth();
  const clearUser = useAuthStore((s) => s.clearUser);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const persisterCleanupRef = useRef<(() => void) | null>(null);

  // Read homePage from the user object directly, NOT from the Zustand store.
  // `useAuth` returns user from the React Query cache, which can update one
  // render before Zustand is hydrated (setUser fires in useAuth's useEffect,
  // which runs after the render commit). Reading homePage from the store in
  // this window produces a mismatch — user is fresh, store is stale-empty —
  // and the loop-breaker below would mis-fire because target falls back to
  // "/" (= pathname after a post-login navigate to "/"), tripping the "no
  // accessible route" branch and wiping the session.
  //
  // Permissions used to need the same care for the same reason. They no longer
  // can: `canAccessRoute` reads them off the `user` it is handed, so the fresh
  // user carries its own permission set and the two cannot disagree.
  const userHomePage = homePathFor(user ?? null);

  // Runtime auth + permission enforcement. Catches:
  // - whoami 401 clearing the store after the initial optimistic load
  // - permission changes during the session (role demotion etc.)
  // - the post-hydration first render after a hard refresh, since the
  //   layout's beforeLoad ran before Zustand was populated and skipped
  //   the permission check
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      // replace: true — back-button shouldn't return to a route the user
      // is no longer authenticated for.
      void navigate({to: '/login', replace: true});
      return;
    }
    if (user && !canAccessRoute(location.pathname, user)) {
      const target = userHomePage;
      // Same loop-breaker as in beforeLoad — if the redirect target is also
      // inaccessible, force logout and go to /login to avoid hammering the
      // navigate queue. replace: true on both branches so the denied route
      // doesn't sit in history.
      if (target !== location.pathname && canAccessRoute(target, user)) {
        void navigate({to: target, replace: true});
      } else {
        clearUser();
        void navigate({to: '/login', replace: true});
      }
    }
  }, [isLoading, isAuthenticated, user, location.pathname, userHomePage, clearUser, navigate]);

  useEffect(() => {
    if (!user?.id) return;

    const persister = createIDBPersister(user.id);
    const [unsubscribe] = persistQueryClient({
      queryClient,
      persister,
      maxAge: PERSIST_MAX_AGE,
      // Discard cache written by a previous build. Without this, a new deploy
      // rehydrates old-shaped bodies into new code and crashes reads that
      // assume the current shape (DERP: sidebar inbox count white-screen).
      // Imported, not referenced inline: the raw `__CACHE_BUSTER__` define is
      // unsubstituted in dev and needs a guard — see src/lib/cache-buster.ts.
      buster: CACHE_BUSTER,
    });

    persisterCleanupRef.current = unsubscribe;

    return () => {
      unsubscribe();
      persisterCleanupRef.current = null;
    };
  }, [user?.id, queryClient]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-white" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <AuthenticatedLayout />
      <Toaster />
    </>
  );
}
